import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, Clock, CheckCircle2, Wallet, Sprout, FlaskConical, ArrowRight } from "lucide-react";
import api, { fmtErr } from "@/lib/api";
import { useLang, LangToggle } from "@/lib/i18n";
import { inr, fmtDate, StatusBadge } from "@/lib/utils";

const ACTIVE = ["Confirmed", "Payment Pending", "Payment Completed", "Preparing for Delivery", "Out for Delivery", "Quantity Modified"];
const PENDING = ["Order Placed", "Under Review"];

export default function CustomerDashboard() {
  const { t } = useLang();
  const [orders, setOrders] = useState([]);
  const [err, setErr] = useState("");
  const [dealerContact, setDealerContact] = useState("");

  useEffect(() => {
    Promise.all([
      api.get("/orders/mine"),
      api.get("/public/business").catch(() => ({ data: {} })),
      api.get("/notifications/mine").catch(() => ({ data: [] }))
    ])
      .then(([ordersRes, businessRes]) => {
        const list = ordersRes.data || [];
        setOrders(list);
        setDealerContact(businessRes.data?.customer_contact_number || "");
 
      })
      .catch((e) => setErr(fmtErr(e)));
  }, []);

  const pending = orders.filter((o) => PENDING.includes(o.status)).length;
  const active = orders.filter((o) => ACTIVE.includes(o.status)).length;
  const completed = orders.filter((o) => ["Delivered", "Completed"].includes(o.status)).length;
  const due = orders.reduce((s, o) => s + (Number(o.payment?.due) || 0), 0);

  const cards = [
    { label: t("pendingApproval"), value: pending, icon: Clock, testid: "card-pending-orders" },
    { label: t("activeConfirmed"), value: active, icon: ClipboardList, testid: "card-active-orders" },
    { label: t("completed"), value: completed, icon: CheckCircle2, testid: "card-completed-orders" },
    { label: t("outstandingDue"), value: inr(due), icon: Wallet, testid: "card-outstanding-due" },
  ];

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      <div className="flex items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">{t("dashboard")}</h1>
          <p className="text-sm text-slate-500 mt-0.5">{t("dashboardSubtitle")}</p>
        </div>
        <LangToggle />
      </div>

      {err && <div className="text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-lg p-3">{err}</div>}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {cards.map((c) => (
          <div key={c.label} className="sf-card p-5" data-testid={c.testid}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-bold uppercase tracking-wider text-slate-500">{c.label}</span>
              <c.icon className="h-4 w-4 text-slate-400" />
            </div>
            <div className="font-display text-3xl font-extrabold text-slate-900 mt-2">{c.value}</div>
          </div>
        ))}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Link to="/customer/fertilizer" data-testid="quick-order-fertilizer" className="sf-card p-6 flex items-center justify-between hover:shadow-md transition-shadow group">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-lg bg-[#0B192C] flex items-center justify-center">
              <Sprout className="h-5 w-5 text-white" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">{t("quickOrderFertilizer")}</div>
              <div className="text-sm text-slate-500">{t("quickOrderFertilizerSub")}</div>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-400 group-hover:text-[#0B192C] transition-colors" />
        </Link>
        <Link to="/customer/pesticide" data-testid="quick-order-pesticide" className="sf-card p-6 flex items-center justify-between group">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-lg bg-slate-100 flex items-center justify-center">
              <FlaskConical className="h-5 w-5 text-slate-500" />
            </div>
            <div>
              <div className="font-display font-bold text-slate-900">
                {t("quickOrderPesticide")}{" "}
                <span className="ml-1 rounded-full bg-amber-50 border border-amber-200 px-2 py-0.5 text-[10px] font-bold text-amber-800 uppercase tracking-wider">{t("comingSoon")}</span>
              </div>
              <div className="text-sm text-slate-500">{t("quickOrderPesticideSub")}</div>
            </div>
          </div>
          <ArrowRight className="h-5 w-5 text-slate-400" />
        </Link>
      </div>

      <div className="sf-card overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between">
          <h2 className="font-display font-bold text-slate-900">{t("recentOrders")}</h2>
          <Link to="/customer/orders" className="sf-link text-sm">
            {t("viewAll")}
          </Link>
        </div>
        <table className="w-full">
          <thead>
            <tr>
              <th className="sf-th">{t("orderNo")}</th>
              <th className="sf-th">{t("date")}</th>
              <th className="sf-th">{t("products")}</th>
              <th className="sf-th">{t("total")}</th>
              <th className="sf-th">{t("status")}</th>
            </tr>
          </thead>
          <tbody>
            {orders.slice(0, 6).map((o) => (
              <tr key={o.id} className="sf-tr">
                <td className="sf-td font-mono-sf font-semibold text-slate-900">{o.order_number}</td>
                <td className="sf-td">{fmtDate(o.created_at)}</td>
                <td className="sf-td">{o.items.length}</td>
                <td className="sf-td">{o.payment.total > 0 ? inr(o.payment.total) : "Rate pending"}</td>
                <td className="sf-td">
                  <StatusBadge status={o.status} />
                </td>
              </tr>
            ))}
            {orders.length === 0 && (
              <tr>
                <td className="sf-td text-slate-400" colSpan={5}>
                  No orders yet — place your first fertilizer order.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
